// ===================================
// FIXED PROFILE MENU COMPONENT - src/components/ProfileMenu.tsx
// ===================================
import React, { useState, useRef, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { 
  User, 
  LogOut, 
  Settings, 
  Building2, 
  Shield, 
  ChevronDown 
} from 'lucide-react';

interface ProfileMenuProps {
  compact?: boolean;
}

const ProfileMenu: React.FC<ProfileMenuProps> = ({ compact = false }) => {
  const [isOpen, setIsOpen] = useState(false);
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const menuRef = useRef<HTMLDivElement>(null);

  const handleLogout = () => {
    logout();
    navigate('/login');
    setIsOpen(false);
  };

  const handleProfileSettings = () => {
    navigate('/profile');
    setIsOpen(false);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  if (!user) return null;

  if (compact) {
    // Compact version for sidebar
    return (
      <div className="space-y-2">
        <button
          onClick={handleProfileSettings}
          className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
        >
          <Settings className="h-4 w-4" />
          Profile Settings
        </button>
        <button
          onClick={handleLogout}
          className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-700 hover:bg-red-50 rounded-lg transition-colors"
        >
          <LogOut className="h-4 w-4" />
          Sign Out
        </button>
      </div>
    );
  }

  // Full dropdown version for header
  return (
    <div className="relative" ref={menuRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 p-1 text-sm text-gray-700 hover:bg-gray-50 rounded-lg transition-colors"
      >
        <ChevronDown className={`h-4 w-4 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-80 bg-white rounded-lg shadow-lg ring-1 ring-black ring-opacity-5 z-50">
          {/* User Info Header */}
          <div className="px-4 py-3 border-b border-gray-200">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center text-white font-medium">
                {user.name.charAt(0).toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">{user.name}</p>
                <p className="text-xs text-gray-500 truncate">{user.email}</p>
              </div>
            </div>
          </div>

          {/* User Details */}
          <div className="px-4 py-3 border-b border-gray-200 space-y-2">
            <div className="flex items-center gap-2 text-xs">
              <Building2 className="h-3 w-3 text-gray-400" />
              <span className="text-gray-600">Business:</span>
              <span className="font-medium">{user.current_business}</span>
            </div>
            <div className="flex items-center gap-2 text-xs">
              <Shield className="h-3 w-3 text-gray-400" />
              <span className="text-gray-600">Role:</span>
              <span className="font-medium">{user.role_name}</span>
              {user.is_admin && (
                <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                  Admin
                </span>
              )}
            </div>
            <div className="flex items-center gap-2 text-xs">
              <User className="h-3 w-3 text-gray-400" />
              <span className="text-gray-600">Department:</span>
              <span className="font-medium">{user.department_name || 'Not assigned'}</span>
            </div>
          </div>

          {/* Permissions */}
          <div className="px-4 py-3 border-b border-gray-200">
            <p className="text-xs font-medium text-gray-900 mb-2">Permissions</p>
            <div className="flex flex-wrap gap-1">
              {user.permission.split(',').map((perm) => (
                <span
                  key={perm.trim()}
                  className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800"
                >
                  {perm.trim()}
                </span>
              ))}
            </div>
          </div>

          {/* Menu Items */}
          <div className="py-1">
            <button
              onClick={handleProfileSettings}
              className="flex items-center gap-2 w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
            >
              <Settings className="h-4 w-4" />
              Profile Settings
            </button>
            <button
              onClick={handleLogout}
              className="flex items-center gap-2 w-full px-4 py-2 text-sm text-red-700 hover:bg-red-50 transition-colors"
            >
              <LogOut className="h-4 w-4" />
              Sign Out
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProfileMenu;